import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../services/api';
import './ForgotPassword.css';

const ForgotPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const emailFromLink = searchParams.get('email');
  const tempPasswordFromLink = searchParams.get('tempPassword');
  
  const [step] = useState<'request' | 'reset'>(emailFromLink && tempPasswordFromLink ? 'reset' : 'request');
  const [email, setEmail] = useState(emailFromLink || '');
  const [tempPassword, setTempPassword] = useState(tempPasswordFromLink || '');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const validatePassword = (password: string): string | null => {
    if (password.length < 8) {
      return '비밀번호는 8자리 이상이어야 합니다.';
    }

    let complexityCount = 0;
    if (/[A-Z]/.test(password)) complexityCount++;
    if (/[a-z]/.test(password)) complexityCount++;
    if (/[0-9]/.test(password)) complexityCount++;
    if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) complexityCount++;

    if (complexityCount < 3) {
      return '비밀번호는 대문자, 소문자, 숫자, 특수문자 중 3가지 이상을 조합해야 합니다.';
    }

    return null;
  };

  const handleRequestReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setIsLoading(true);

    try {
      await api.post('/auth/forgot-password', { email });
      setSuccess('임시 비밀번호가 이메일로 발송되었습니다. 이메일을 확인해주세요.');
    } catch (err: any) {
      setError(err.response?.data?.message || '비밀번호 초기화 요청에 실패했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // 비밀번호 검증
    const passwordError = validatePassword(newPassword);
    if (passwordError) {
      setError(passwordError);
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('새 비밀번호와 비밀번호 확인이 일치하지 않습니다.');
      return;
    }

    setIsLoading(true);

    try {
      await api.post('/auth/reset-password', {
        email,
        tempPassword,
        newPassword,
      });
      setSuccess('비밀번호가 성공적으로 변경되었습니다. 로그인 페이지로 이동합니다.');
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch (err: any) {
      setError(err.response?.data?.message || '비밀번호 변경에 실패했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="forgot-password-container">
      <div className="forgot-password-card">
        <h2>{step === 'request' ? '비밀번호 찾기' : '비밀번호 재설정'}</h2>
        
        {step === 'request' ? (
          <form onSubmit={handleRequestReset}>
            <div className="form-group">
              <label htmlFor="email">이메일</label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="example@vntgcorp.com"
              />
              <small className="hint">
                가입하신 이메일 주소를 입력하시면 임시 비밀번호가 발송됩니다.
              </small>
            </div>
            {error && <div className="error-message">{error}</div>}
            {success && <div className="success-message">{success}</div>}
            <button type="submit" disabled={isLoading} className="submit-button">
              {isLoading ? '처리 중...' : '임시 비밀번호 발송'}
            </button>
          </form>
        ) : (
          <form onSubmit={handleResetPassword}>
            <div className="form-group">
              <label htmlFor="email">이메일</label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="example@vntgcorp.com"
              />
            </div>
            <div className="form-group">
              <label htmlFor="tempPassword">임시 비밀번호</label>
              <input
                id="tempPassword"
                type="password"
                value={tempPassword}
                onChange={(e) => setTempPassword(e.target.value)}
                required
                placeholder="이메일로 받은 임시 비밀번호"
              />
            </div>
            <div className="form-group">
              <label htmlFor="newPassword">새 비밀번호</label>
              <input
                id="newPassword"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
              />
              <small className="hint">
                8자리 이상, 대문자/소문자/숫자/특수문자 중 3가지 이상 조합
              </small>
            </div>
            <div className="form-group">
              <label htmlFor="confirmPassword">새 비밀번호 확인</label>
              <input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
              />
            </div>
            {error && <div className="error-message">{error}</div>}
            {success && <div className="success-message">{success}</div>}
            <button type="submit" disabled={isLoading} className="submit-button">
              {isLoading ? '처리 중...' : '비밀번호 변경'}
            </button>
          </form>
        )}
        
        <div className="login-link">
          <a href="/login">로그인으로 돌아가기</a>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;

